import { Box, Card, Link, Skeleton, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as ReactRouterLink } from 'react-router-dom';

import { Alert, Text, api, getQueryProps, isDefined, isTruthy } from 'common';
import { BlockCategory } from 'vivaa-api';

import { ScheduledTriggers } from './ScheduledTriggers';
import { Triggers } from './Triggers';
import { FeatureFlagKey, useFlag } from '../../../../../../../utils/flags';
import { useFlow } from '../../../../../Details/Flow/useFlow';
import { BlockCategoryIcon } from '../../../../BlockIcons/BlockCategories.tsx';
import { FlowDefinitionStateTag } from '../../../../Definitions/common/StateTag.tsx';
import { useFlowDefinitionContext } from '../../../../context.ts';

export function StartNodeBlockContent() {
  const { flowId } = useFlow();
  const { flowDefinition } = useFlowDefinitionContext();
  const isScheduledFlowTriggersEnabled = useFlag({
    flagKey: FeatureFlagKey.scheduledFlowTriggers,
  });
  const { data, isLoading, isSuccess, error } = useQuery({
    ...getQueryProps(api.client.Scenario, 'findAll', {
      output: api.Scenario.OutputKind.Flows,
      outputIncludes: flowId,
    }),
  });

  // Get the parent flows that use this flow as a subflow
  const parentFlowLimit = 10;
  const { data: parentFlowData, isLoading: isParentFlowsLoading } = useQuery({
    ...getQueryProps(api.client.FlowDefinition, 'findSubFlowParents', {
      flowDefinitionId: flowDefinition.id,
      limit: parentFlowLimit + 1, // +1 so we know if there's more than the limit
      orderBy: 'name',
    }),
    enabled: isTruthy(flowDefinition.id),
  });

  return (
    <Stack spacing={2}>
      <Triggers flowId={flowId} />
      {isScheduledFlowTriggersEnabled && (
        <ScheduledTriggers flowId={flowId} flowDefinitionId={flowDefinition.id} />
      )}
      {isLoading && <Skeleton variant="rectangular" height={40} />}
      {error && <Alert title="Unable to load scenarios" variant="error" error={error} />}
      {isSuccess && data.length === 0 && (
        <Alert title="Connect Flow to Scenarios" variant="warning">
          <Stack gap={2}>
            <Text variant="body2">To link scenarios to this flow, follow these simple steps:</Text>
            <Text variant="body2">
              <Box component="ol" paddingLeft={2}>
                <li>Go to the details page of the scenario you want to connect.</li>
                <li>Look for an option called “output” for Flows.</li>
                <li>Choose the flow you want to connect to.</li>
                <li>Do this for each scenario you want to link to this flow.</li>
              </Box>
            </Text>
            <Link
              component={ReactRouterLink}
              to="/fingerprint/scenarios"
              target="_blank"
              rel="noopener noreferrer"
            >
              View Scenarios
            </Link>
          </Stack>
        </Alert>
      )}
      {isSuccess &&
        data.map((scenario) => {
          const { id, name, description } = scenario;
          return (
            <Card key={id} variant="outlined">
              <Stack paddingX={1.5} paddingY={1} gap={1}>
                <Text variant="body2">
                  <Link
                    component={ReactRouterLink}
                    to={`/fingerprint/scenarios/${id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {name}
                  </Link>
                </Text>
                <Text variant="body2" whiteSpace="pre-wrap">
                  {description || 'No description'}
                </Text>
              </Stack>
            </Card>
          );
        })}

      {isParentFlowsLoading && <Skeleton variant="rectangular" height={40} />}
      {isDefined(parentFlowData) && parentFlowData.length > 0 && (
        <Stack spacing={2}>
          <Text variant="heading6">Parent Flows:</Text>

          {parentFlowData
            .filter((_, index) => index < parentFlowLimit)
            .map((parentFlow) => {
              const { id, name, description } = parentFlow;
              return (
                <Card key={id} variant="outlined">
                  <Stack paddingX={1.5} paddingY={1} gap={1}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box display="flex" alignItems="center" gap={1}>
                        <BlockCategoryIcon blockCategory={BlockCategory.Logic} />
                        <Text variant="body2">
                          <Link
                            component={ReactRouterLink}
                            to={`/agents/flows/${parentFlow.flowId}/definitions/${parentFlow.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {name}
                          </Link>
                        </Text>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        v{parentFlow.version} <FlowDefinitionStateTag state={parentFlow.state} />
                      </Box>
                    </Box>
                    {description && (
                      <Text variant="body2" whiteSpace="pre-wrap">
                        {description}
                      </Text>
                    )}
                  </Stack>
                </Card>
              );
            })}
          {parentFlowData.length > 0 && (
            <Text
              variant="body2"
              title="Click to show all Parent flows in a new tab..."
              sx={{ textAlign: 'right' }}
            >
              1-{Math.min(parentFlowData.length, parentFlowLimit)} of{' '}
              <Link
                component={ReactRouterLink}
                to={`/agents/flows?organizationId=${flowDefinition.organizationId}&includesSubFlowDefinitionId=${flowDefinition.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {parentFlowData.length <= parentFlowLimit
                  ? parentFlowData.length
                  : `more than ${parentFlowLimit}`}{' '}
                Parent Flows
              </Link>
            </Text>
          )}
        </Stack>
      )}
    </Stack>
  );
}
